/**
 * THE IMAGE PRO ACADEMY — the page that replaced Events (Peter, 2026-08-26:
 * "i do not want to create an event features, perhaps we should remove event
 * or change it to the pro academy").
 *
 * WHY EVENTS DIED, measured before deleting: public.events holds THREE rows
 * ever, all on retired sharon_ma, newest June 2026 — and NO surface anywhere
 * writes the table (image-os-web and ops-ios both grepped clean). It was a
 * read-only page over data nothing produces: a promise to pros that owners
 * had no way to keep.
 *
 * WHAT THIS IS INSTEAD: the Academy, live. The corporate page sends no
 * X-Frame-Options and no CSP (probed 2026-08-26), so it embeds — but that is
 * corporate's header to change, so "Open the Academy" lives OUTSIDE the frame
 * and is always visible: if framing is ever blocked this degrades to a
 * labelled door, not a silent dead box. No sandbox attribute: it is IMAGE's
 * own first-party site and sandboxing would break its scripts.
 *
 * The old imagestudios360.com address 301s to imagestudios.co (probed) —
 * this page and Home's card both carry the current domain directly.
 *
 * UPCOMING CLASSES (ACADEMY-1, Peter 2026-10-28): corporate's live classes,
 * read daily from its registration form by the backend, sit above the frame
 * as cards — Register opens corporate's form prefilled with the pro and the
 * class ticked; Going is the pro's own tap, which the owner can see. The
 * studio's reminder texts link here with ?s=<class>, which lifts that card.
 * The cards appear only once the studio's switch is on (academy_upcoming is
 * empty otherwise), so this page is exactly what it was until then.
 */

import { useSearchParams } from 'react-router-dom';
import { useAcademySessions, useSetGoing, registerUrl, whenLabel, daysUntil, type AcademySession } from '@/lib/academy';
import { usePro, useProLocations } from '@/lib/proLocations';
import { useActiveLocationId } from '@/lib/locationContext';
import { viewAsProId } from '@/lib/viewAs';

export const ACADEMY_URL = 'https://imagestudios.co/image-pro-academy/';

function SessionCard({ s, lifted, pro, studio }: {
  s: AcademySession;
  lifted: boolean;
  pro: { name: string; email: string };
  studio: string;
}) {
  const setGoing = useSetGoing();
  const viewing = !!viewAsProId();
  const days = daysUntil(s.starts_at);
  const soon = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : days > 1 ? `In ${days} days` : null;
  return (
    <li
      data-testid="academy-session"
      data-session={s.id.slice(0, 8)}
      className={`rounded-2xl bg-white ring-1 px-4 py-3.5 space-y-2 ${lifted ? 'ring-2 ring-image-pink' : 'ring-image-border'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-sm text-image-black leading-snug">{s.title}</p>
          <p className="text-xs text-image-charcoal mt-0.5">
            {whenLabel(s.starts_at)}
            {soon && <span className="text-image-pink font-semibold"> &middot; {soon}</span>}
          </p>
          {s.time_conflict && (
            <p className="text-[11px] text-image-charcoal mt-0.5">
              The form lists two different times for this class &mdash; check the time when you register.
            </p>
          )}
        </div>
        {s.going && (
          <span className="shrink-0 text-[11px] font-bold text-white bg-image-pink rounded-full px-2 py-0.5">Going</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <a
          href={registerUrl(s, { ...pro, studio })}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-xl bg-image-black text-white text-xs font-bold px-3.5 py-2"
        >
          Register
        </a>
        <button
          type="button"
          disabled={viewing || setGoing.isPending}
          onClick={() => setGoing.mutate({ sessionId: s.id, going: !s.going })}
          className="inline-flex items-center justify-center rounded-xl ring-1 ring-image-border text-xs font-bold px-3.5 py-2 text-image-black disabled:opacity-50"
        >
          {s.going ? 'Not going' : "I'm going"}
        </button>
        {setGoing.isError && (
          <span className="text-[11px] text-image-charcoal">{(setGoing.error as Error).message}</span>
        )}
      </div>
    </li>
  );
}

export function UpcomingSessions() {
  const { data: sessions } = useAcademySessions();
  const { data: pro } = usePro();
  const { data: locations } = useProLocations();
  const { activeLocationId } = useActiveLocationId();
  const [params] = useSearchParams();
  const lifted = params.get('s') ?? '';
  if (!sessions?.length) return null;
  const studio = locations?.find((l) => l.location_id === activeLocationId)?.display_name ?? '';
  const who = {
    name: [pro?.first_name, pro?.last_name].filter(Boolean).join(' '),
    email: pro?.email ?? '',
  };
  return (
    <section data-testid="academy-upcoming" className="space-y-2">
      <h2 className="font-bold text-sm text-image-black">Upcoming live classes</h2>
      <ul className="space-y-2">
        {sessions.map((s) => (
          <SessionCard key={s.id} s={s} lifted={!!lifted && s.id.startsWith(lifted)} pro={who} studio={studio} />
        ))}
      </ul>
      <p className="text-[11px] text-image-charcoal">
        Classes are run by IMAGE Pro Academy; your studio texts you a week and two days before each one.
      </p>
    </section>
  );
}

export function AcademySection() {
  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-image-black">
            IMAGE <span className="text-image-pink">Pro Academy</span>
          </h1>
          <p className="text-sm text-image-slate mt-0.5">
            Education, content and perks for beauty professionals — straight from IMAGE.
          </p>
        </div>
        <a
          href={ACADEMY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold text-image-pink hover:text-image-pink-dark whitespace-nowrap"
        >
          Open the Academy ↗
        </a>
      </div>
      <iframe
        src={ACADEMY_URL}
        title="IMAGE Pro Academy"
        loading="lazy"
        className="w-full h-[78vh] rounded-2xl border border-image-border bg-white"
      />
    </div>
  );
}

export default function Academy() {
  return (
    <div className="space-y-4">
      <UpcomingSessions />
      <AcademySection />
    </div>
  );
}
